import styled from '@emotion/styled'
import Loadable from '@loadable/component'
import slugify from '@sindresorhus/slugify'
import { useIntl } from 'gatsby-plugin-react-intl'
import React, { FC, Fragment, useEffect, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'

import supabase from '../../../../client'
import Survey from '../../../../components/Survey'
import { useIntlLabel } from '../../../../intl'
import { ServiceStepType, ratingTooltipArray } from '../../../../types'
import {
    SectionRoot,
    ServiceDescription,
    ServiceStepCase,
    ServiceStepCategory,
    ServiceStepDescription,
    ServiceStepTitle,
    ServiceStepWrapper,
} from './_styles'

interface IProps {
    serviceName: string
    serviceHint?: string
    serviceDescription?: string
    serviceSteps?: ServiceStepType[]
    printRef: React.RefObject<HTMLDivElement> | null
    serviceHistory: string
    serviceHierarchy: string
    categoryName: String
    slug: string
}

const Title = styled.h2((props) => {
    return {
        fontWeight: 'bold',
        marginTop: 0,
    }
})

const ServiceContent: FC<IProps> = ({
    serviceName,
    serviceHint = '',
    serviceDescription = '',
    serviceSteps = [],
    printRef,
    serviceHistory = '',
    serviceHierarchy = '',
    categoryName = '',
    slug = '',
}) => {
    const [rate, setRate] = useState(0)
    const [raters, setRaters] = useState(0)
    const LoadableRating = Loadable(() => import('../../../../components/Rating'))

    useEffect(() => {
        getRating()
    }, [])

    const getRating = async () => {
        let { data: ratings, error } = await supabase.from('ratings').select().eq('slug', slug)

        const nonZeroRatings = ratings?.filter((rating) => rating.rate !== 0)

        if (nonZeroRatings?.length) {
            const totalRate = nonZeroRatings.reduce((sum, rating) => sum + rating.rate, 0)
            const averageRating = totalRate / nonZeroRatings.length
            setRate(averageRating)
            setRaters(nonZeroRatings.length)
        }

        if (error) {
            setRate(0)
        }
    }

    const getIntlLabel = useIntlLabel()
    const intl = useIntl()

    return (
        <SectionRoot ref={printRef} fullWidth={serviceSteps.length === 0}>
            <Title>{serviceName}</Title>
            <ServiceDescription>
                <ReactMarkdown linkTarget="_blank" remarkPlugins={[remarkGfm]}>
                    {serviceDescription}
                </ReactMarkdown>
            </ServiceDescription>
            {/*<HomeListStepService serviceName="" serviceSteps={serviceSteps} />*/}
            {serviceSteps.map((stepItm, index) => {
                return (
                    <Fragment key={index}>
                        {/*<AnchorLink hash={`${slugify(stepItm.stepName)}-${index}`} />*/}
                        {stepItm.stepType === 'case' && (
                            <ServiceStepCase>{getIntlLabel(stepItm, 'stepName')}</ServiceStepCase>
                        )}
                        {stepItm.stepType === 'category' && (
                            <ServiceStepCategory>
                                {getIntlLabel(stepItm, 'stepName')}
                            </ServiceStepCategory>
                        )}
                        {stepItm.stepType === 'step' && (
                            <ServiceStepWrapper id={slugify(stepItm.stepName)}>
                                <ServiceStepTitle>
                                    {getIntlLabel(stepItm, 'stepName')}
                                </ServiceStepTitle>
                                <ServiceStepDescription>
                                    <ReactMarkdown linkTarget="_blank" remarkPlugins={[remarkGfm]}>
                                        {getIntlLabel(stepItm, 'stepDescription')}
                                    </ReactMarkdown>
                                </ServiceStepDescription>
                            </ServiceStepWrapper>
                        )}
                    </Fragment>
                )
            })}
            <LoadableRating
                initialValue={rate}
                readonly
                size={32}
                showTooltip
                tooltipDefaultText={intl.formatMessage({ id: 'label_note' })}
                tooltipArray={ratingTooltipArray}
            />
            <span style={{ marginLeft: 8 }}>{raters > 0 && `(${raters})`}</span>
            <div style={{ marginBottom: 20 }}></div>
            <Survey slug={slug ?? ''} />
        </SectionRoot>
    )
}

export default ServiceContent
